import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const ROUTE = '/env/$environmentId/data/aliases';
const percent = (share: number | null) => (share === null ? '—' : `${Math.round(share * 1000) / 10}%`);

/**
 * Data → Name Aliases (BB-29): old spellings of events and attributes rewritten to their canonical name, and the
 * "Legacy names" report that shows when no app version sends an old spelling any more.
 */
export function AliasesPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('catalog.edit');
  const aliases = useQuery({ queryKey: ['aliases', environmentId], queryFn: () => api.listAliases(environmentId) });
  const usage = useQuery({ queryKey: ['alias-usage', environmentId], queryFn: () => api.aliasUsage(environmentId, 30) });
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['aliases', environmentId] }), queryClient.invalidateQueries({ queryKey: ['alias-usage', environmentId] })]);
  const [form, setForm] = useState({ kind: 'event' as 'event' | 'attribute', aliasName: '', canonicalName: '', note: '', migrateExisting: false });
  const create = useMutation({
    mutationFn: () => api.createAlias(environmentId, { kind: form.kind, aliasName: form.aliasName.trim(), canonicalName: form.canonicalName.trim(), ...(form.note.trim() ? { note: form.note.trim() } : {}), migrateExisting: form.migrateExisting }),
    onSuccess: async () => {
      setForm({ ...form, aliasName: '', canonicalName: '', note: '', migrateExisting: false });
      await refresh();
    },
  });
  const archive = useMutation({ mutationFn: (id: string) => api.archiveAlias(environmentId, id), onSuccess: refresh });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.aliasName.trim() && form.canonicalName.trim()) create.mutate();
  };

  return (
    <section>
      <h1>Name aliases</h1>
      <p className="hint">An old spelling of an event or attribute is stored under its canonical name; the original stays in the event as the source name.</p>
      {!canEdit && <ReadOnlyBanner>Changing aliases needs the catalog.edit permission.</ReadOnlyBanner>}
      {aliases.isPending ? (
        <p aria-busy="true">Loading aliases…</p>
      ) : aliases.isError ? (
        <ErrorState title="Aliases could not be loaded" message={errorText(aliases.error)} onRetry={() => void aliases.refetch()} />
      ) : aliases.data.length === 0 ? (
        <EmptyState title="No aliases" description="Add one when apps send the same event or attribute under different spellings." />
      ) : (
        <table className="table" aria-label="Aliases">
          <thead>
            <tr>
              <th scope="col">Old spelling</th>
              <th scope="col">Canonical</th>
              <th scope="col">Kind</th>
              <th scope="col">Source</th>
              <th scope="col">Received 30d</th>
              <th scope="col">Last seen</th>
              {canEdit && <th scope="col">Archive</th>}
            </tr>
          </thead>
          <tbody>
            {aliases.data.map((a) => (
              <tr key={a.id}>
                <td>
                  <code>{a.aliasName}</code>
                </td>
                <td>
                  <code>{a.canonicalName}</code>
                </td>
                <td>{a.kind}</td>
                <td>{a.source === 'alias_map' ? 'Alias map' : 'Added by hand'}</td>
                <td>{a.usage30d}</td>
                <td>{a.lastSeenDay ?? '—'}</td>
                {canEdit && (
                  <td>
                    <Button size="sm" variant="danger" onClick={() => archive.mutate(a.id)} disabled={archive.isPending}>
                      Archive {a.aliasName}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {archive.isError && <p role="alert">{errorText(archive.error)}</p>}

      {canEdit && (
        <form aria-label="Add alias" className="stack-form" onSubmit={submit}>
          <label>
            Kind
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'event' | 'attribute' })}>
              <option value="event">Event</option>
              <option value="attribute">Attribute</option>
            </select>
          </label>
          <label>
            Old spelling
            <input value={form.aliasName} onChange={(e) => setForm({ ...form, aliasName: e.target.value })} placeholder="signal-new" />
          </label>
          <label>
            Canonical name
            <input value={form.canonicalName} onChange={(e) => setForm({ ...form, canonicalName: e.target.value })} placeholder="signal_new" />
          </label>
          <label>
            Note
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
          <label>
            <input type="checkbox" checked={form.migrateExisting} onChange={(e) => setForm({ ...form, migrateExisting: e.target.checked })} /> Rewrite data already stored under the old spelling
          </label>
          <Button type="submit" variant="primary" disabled={create.isPending || !form.aliasName.trim() || !form.canonicalName.trim()}>
            Add alias
          </Button>
          {create.isError && <p role="alert">{errorText(create.error)}</p>}
        </form>
      )}

      <h2>Legacy names</h2>
      <p className="hint">How often the old spellings still arrive in the last 30 days; an alias at 0 on every supported app version can be archived.</p>
      {usage.isPending ? (
        <p aria-busy="true">Loading usage…</p>
      ) : usage.isError ? (
        <ErrorState title="Usage could not be loaded" message={errorText(usage.error)} />
      ) : usage.data.items.length === 0 ? (
        <p className="hint">No aliases yet.</p>
      ) : (
        <table className="table" aria-label="Legacy names">
          <thead>
            <tr>
              <th scope="col">Canonical</th>
              <th scope="col">Old spelling</th>
              <th scope="col">Received</th>
              <th scope="col">Share</th>
              <th scope="col">App versions</th>
            </tr>
          </thead>
          <tbody>
            {usage.data.items.flatMap((group) =>
              group.aliases.map((a) => (
                <tr key={`${group.kind}:${group.canonicalName}:${a.aliasName}`}>
                  <td>
                    <code>{group.canonicalName}</code>
                  </td>
                  <td>
                    <code>{a.aliasName}</code>
                  </td>
                  <td>{a.count}</td>
                  <td>{percent(a.share)}</td>
                  <td>{a.byAppVersion.length ? a.byAppVersion.map((v) => `${v.appVersion || 'server'} (${v.count})`).join(', ') : '—'}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
